import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { currentPublicBrand } from './lib/publicBrand';

import { Toaster } from "./components/ui/sonner";
import ScrollManager from "./components/ScrollManager";

// Public Pages
import HomePage from "./pages/public/HomePage";
import AboutPage from "./pages/public/AboutPage";
import ServicesPage from "./pages/public/ServicesPage";
import CompliancePage from "./pages/public/CompliancePage";
import ContactPage from "./pages/public/ContactPage";
import AccessPortalPage from "./pages/public/AccessPortalPage";
import PricingPage from "./pages/public/PricingPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import TermsPage from "./pages/public/TermsPage";
import ComplaintsPage from "./pages/public/ComplaintsPage";
import WhistleblowingPage from "./pages/public/WhistleblowingPage";
import SafeguardingPage from "./pages/public/SafeguardingPage";
import EqualityPage from "./pages/public/EqualityPage";
import FormCompletionPage from "./pages/public/FormCompletionPage";
import RefereeCompletionPage from "./pages/public/RefereeCompletionPage";
import TrainingUploadPage from "./pages/public/TrainingUploadPage";
import DocumentUploadPage from "./pages/public/DocumentUploadPage";
import ApplyPage from "./pages/public/ApplyPage";
import StaffIdVerifyPage from "./pages/public/StaffIdVerifyPage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import AuthCallback from "./pages/auth/AuthCallback";

// Worker Portal Pages
import WorkerLoginPage from "./pages/worker/WorkerLoginPage";
import WorkerVerifyPage from "./pages/worker/WorkerVerifyPage";
import WorkerDashboard from "./pages/worker/WorkerDashboard";
import WorkerFormPage from "./pages/worker/WorkerFormPage";
import WorkerStaffIntroductionPage from "./pages/worker/WorkerStaffIntroductionPage";
import WorkerPortalErrorBoundary from "./components/worker/WorkerPortalErrorBoundary";

// Client Hub (care homes)
import ClientLoginPage from "./pages/client/ClientLoginPage";
import ClientVerifyPage from "./pages/client/ClientVerifyPage";
import ClientHubPage from "./pages/client/ClientHubPage";

// Portal Pages
import PortalLayout from "./components/portal/PortalLayout";
import { portalNestedRouteElements } from "./config/portalRouteElements";

// Auth Context
import { AuthProvider } from "./context/AuthContext";
import { OrgProvider } from "./context/OrgContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// Session Timeout Warning (P0: 5 E's Error Tolerant)
import SessionTimeoutWarning from "./components/SessionTimeoutWarning";
import PublicChatWidget from './components/public/PublicChatWidget';
import CookieBanner from './components/public/CookieBanner';
function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id during render (NOT in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/recruitment" element={<Navigate to="/" replace />} />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/access" element={<AccessPortalPage />} />
      {/* Tenant-scoped public application entry (creates applicants ONLY inside the resolved workspace) */}
      <Route path="/apply/:workspaceSlug" element={<ApplyPage />} />
      <Route path="/verify/staff/:token" element={<StaffIdVerifyPage />} />
      {/* Generic /apply stays non-creating — safe redirect, no unscoped applicant path */}
      <Route path="/apply" element={<Navigate to="/contact?type=platform_demo" replace />} />
      <Route path="/request-care" element={<Navigate to="/contact?type=platform_demo" replace />} />
      <Route path="/request-staffing" element={<Navigate to="/contact?type=platform_demo" replace />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/complaints" element={<ComplaintsPage />} />
      <Route path="/whistleblowing" element={<WhistleblowingPage />} />
      <Route path="/safeguarding" element={<SafeguardingPage />} />
      <Route path="/equality" element={<EqualityPage />} />
      
      {/* Public Form Completion Route (No Auth Required) */}
      <Route path="/f/:shortCode" element={<FormCompletionPage />} />
      <Route path="/forms/complete/:token" element={<FormCompletionPage />} />
      
      {/* Public Referee Reference Completion Routes (No Auth Required) */}
      <Route path="/r/:shortCode" element={<RefereeCompletionPage />} />
      <Route path="/referee/complete/:token" element={<RefereeCompletionPage />} />
      
      {/* Public Training Certificate Upload Route (No Auth Required) */}
      <Route path="/training/upload/:token" element={<TrainingUploadPage />} />
      
      {/* Public Document Upload Route (No Auth Required - From Email Links) */}
      <Route path="/u/:shortCode" element={<DocumentUploadPage />} />
      <Route path="/upload-document" element={<DocumentUploadPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/careops/login" element={<LoginPage mode="platform" />} />
      <Route path="/platform/login" element={<Navigate to="/careops/login" replace />} />
      <Route path="/portal/login" element={<Navigate to="/login" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Worker Portal Routes (No Admin Auth Required) */}
      <Route path="/workforce/:workspaceSlug" element={<WorkerLoginPage />} />
      <Route path="/worker/login" element={<WorkerLoginPage />} />
      <Route path="/worker/verify" element={<WorkerVerifyPage />} />
      <Route path="/a/:shortCode" element={<WorkerVerifyPage />} />
      <Route
        path="/worker/dashboard"
        element={(
          <WorkerPortalErrorBoundary>
            <WorkerDashboard />
          </WorkerPortalErrorBoundary>
        )}
      />
      <Route path="/worker/forms/:formId" element={<WorkerFormPage />} />
      <Route path="/worker/staff-introduction" element={<WorkerStaffIntroductionPage />} />

      {/* Client Hub — care home transparency */}
      <Route path="/client/login" element={<ClientLoginPage />} />
      <Route path="/client/verify" element={<ClientVerifyPage />} />
      <Route path="/client/hub" element={<ClientHubPage />} />
      
      {/* Portal Routes - Protected */}
      <Route path="/portal/*" element={
        <ProtectedRoute>
          <PortalLayout />
        </ProtectedRoute>
      }>
        {portalNestedRouteElements}
      </Route>
    </Routes>
  );
}


// A host-locked design preview. It does not initialise auth, tenant context,
// public chat, API-backed providers, or worker/admin/medical upload routes.
function DesignPreviewRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      {/* Public CareDock holding pages are safe to preview: no forms or APIs. */}
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={
        <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
          <section className="max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center">
            <h1 className="text-2xl font-semibold text-[#0D274E]">Design preview only</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Sign-in, form submission and staff data are not available on this preview.
            </p>
            <a href="/" className="mt-6 inline-flex rounded-lg bg-[#0D274E] px-5 py-3 text-sm font-semibold text-white">View homepage</a>
          </section>
        </main>
      } />
    </Routes>
  );
}

function App() {
  if (currentPublicBrand().isPreview) {
    return <BrowserRouter><DesignPreviewRoutes /></BrowserRouter>;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <OrgProvider>
          <AppRouter />
          <ScrollManager />
          <Toaster position="top-right" richColors />
          {/* P0 FIX: Session timeout warning for 5 E's Error Tolerance */}
          <SessionTimeoutWarning />
          <PublicChatWidget />
          <CookieBanner />
        </OrgProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
